import { useEffect, useMemo } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { useFirebaseContext } from '../lib/firebase-context';
import { useAppContext } from '../lib/app-context';
import { NO_PRINT_ID } from '../lib/constants';

const PRINTER_ID_KEY = 'printerId';

const CheckInDate = () => {
  const { activeCheckInDate, attendees, printers, activeEvent } =
    useFirebaseContext();
  const { activeView, setActiveView, printerId, setPrinterId } =
    useAppContext();
  const hideCheckout = Boolean(activeEvent?.hideCheckout);

  useEffect(() => {
    // Set the printer ID from localStorage if it exists
    const storedPrinterId = localStorage.getItem(PRINTER_ID_KEY);
    if (storedPrinterId) {
      setPrinterId(Number(storedPrinterId));
    }
  }, [setPrinterId]);

  // The "Checked Out" filter button is hidden while the event's
  // `hideCheckout` flag is on; bounce off that view if it was active
  // (e.g. the flag flipped mid-session, or the view was set before an
  // event switch), otherwise the list stays filtered with no visible
  // control to clear it.
  useEffect(() => {
    if (hideCheckout && activeView === 'checkedOut') {
      setActiveView('total');
    }
  }, [hideCheckout, activeView, setActiveView]);

  const formattedDate = useMemo(
    () =>
      !activeCheckInDate?.date
        ? ''
        : activeCheckInDate?.date.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
    [activeCheckInDate?.date]
  );

  const stats = useMemo(() => {
    if (!activeCheckInDate) {
      return {
        total: attendees.length,
        checkedIn: 0,
        checkedOut: 0,
        pending: 0,
      };
    }
    // Filter the raw checkedInRefs / checkedOutRefs against the live
    // attendee set. Refs left over from soft-deleted attendees (e.g. after
    // a VBS cancellation or the volunteer-import cleanup script) would
    // otherwise inflate the counters even though the matching rows are
    // hidden from every other view.
    const liveRefs = new Set(attendees.map((a) => a.ref));
    const { checkedInRefs, checkedOutRefs } = activeCheckInDate;
    const checkedInLive = (checkedInRefs ?? []).filter((r) => liveRefs.has(r));
    const checkedOutLive = (checkedOutRefs ?? []).filter((r) =>
      liveRefs.has(r),
    );
    const pending = attendees.filter(
      (attendee) =>
        !checkedInRefs?.includes(attendee.ref) &&
        !checkedOutRefs?.includes(attendee.ref),
    ).length;
    return {
      total: attendees.length,
      // With checkout hidden, "Checked In" means "came today" — don't
      // subtract kids left in `checkedOutRefs` from before the flag was
      // turned on.
      checkedIn: hideCheckout
        ? checkedInLive.length
        : checkedInLive.length - checkedOutLive.length,
      checkedOut: checkedOutLive.length,
      pending,
    };
  }, [attendees, activeCheckInDate, hideCheckout]);

  const handlePrinterSelect = (event: SelectChangeEvent<number>) => {
    setPrinterId(Number(event.target.value));
    // Store the selected printer ID in localStorage
    // so that a user doesn't have to select it every time
    localStorage.setItem(PRINTER_ID_KEY, String(event.target.value));
  };

  if (!activeCheckInDate) {
    return (
      <Alert severity='error'>
        Please contact admin to activate a check-in date
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant='h6' sx={{ whiteSpace: 'nowrap' }}>
          {formattedDate}
        </Typography>
        <ButtonGroup size='medium'>
          <Button
            color='secondary'
            variant={activeView === 'total' ? 'contained' : 'outlined'}
            onClick={() => setActiveView('total')}
          >
            Total: {stats.total}
          </Button>
          <Button
            color='success'
            variant={activeView === 'checkedIn' ? 'contained' : 'outlined'}
            onClick={() => setActiveView('checkedIn')}
          >
            Checked In: {stats.checkedIn}
          </Button>
          {!hideCheckout && (
            <Button
              variant={activeView === 'checkedOut' ? 'contained' : 'outlined'}
              onClick={() => setActiveView('checkedOut')}
            >
              Checked Out: {stats.checkedOut}
            </Button>
          )}
          <Button
            color='warning'
            variant={activeView === 'pending' ? 'contained' : 'outlined'}
            onClick={() => setActiveView('pending')}
          >
            Pending: {stats.pending}
          </Button>
        </ButtonGroup>
        <FormControl size='small' sx={{ minWidth: 220 }}>
          <InputLabel id='printer-select-label'>Printer</InputLabel>
          <Select
            labelId='printer-select-label'
            id='printer-select'
            value={printerId}
            label='Printer'
            onChange={handlePrinterSelect}
          >
            <MenuItem value={0} disabled>
              Select printer
            </MenuItem>
            {printers.map((printer) => (
              <MenuItem key={printer.ref} value={printer.id}>
                {printer.name}
                {printer.id === NO_PRINT_ID ? '' : ` (${printer.id})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );
};

export default CheckInDate;
